// SMS Signup Demo Page
// A publicly accessible page where Twilio reviewers can see and verify the SMS opt-in flow

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, ArrowLeft, CheckCircle, Phone, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';

const SMSSignupPage = () => {
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (phone) {
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              IntakePal
            </span>
          </Link>
          <Link 
            to="/" 
            className="text-sm text-slate-600 hover:text-teal-600 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="bg-slate-900 px-6 py-8 text-center">
            <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Get Case Updates via SMS
            </h1>
            <p className="text-slate-300 text-sm">
              Enter your phone number to receive text message updates about your legal inquiry
            </p>
          </div>

          {/* Form */}
          <div className="p-6">
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Phone Input */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    Mobile Phone Number
                  </label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full text-lg py-3"
                    required
                  />
                </div>

                {/* SMS CONSENT CHECKBOX - OPTIONAL, NOT REQUIRED */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-3 font-medium">Optional: Receive SMS Updates</p>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="sms-consent"
                      checked={smsConsent}
                      onCheckedChange={setSmsConsent}
                      className="mt-1 h-5 w-5 border-2"
                    />
                    <label 
                      htmlFor="sms-consent" 
                      className="text-sm text-slate-700 leading-relaxed cursor-pointer"
                    >
                      I agree to receive text messages about my case from IntakePal and the law firm handling my inquiry. Message frequency: up to 4 messages per inquiry. Message and data rates may apply. Reply STOP to opt out, HELP for help.
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={!phone}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit My Information
                </Button>

                {/* Clarification that SMS is optional */}
                <p className="text-center text-xs text-slate-500">
                  You can submit your information without opting into SMS messages.
                  <br />SMS consent is optional.
                </p>

                {/* Legal Links */}
                <div className="text-center text-xs text-slate-500 space-y-1">
                  <p>
                    By signing up, you agree to our{' '}
                    <Link to="/terms" className="text-teal-600 hover:underline">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-teal-600 hover:underline">Privacy Policy</Link>
                  </p>
                  <p>Mobile numbers are not shared with third parties for marketing purposes.</p>
                </div>
              </form>
            ) : (
              /* Success State */
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Information Submitted!</h2>
                {smsConsent ? (
                  <p className="text-slate-600 mb-4">
                    You have opted in to receive SMS updates about your legal inquiry.
                    <br />Reply STOP at any time to unsubscribe.
                  </p>
                ) : (
                  <p className="text-slate-600 mb-4">
                    Your information has been submitted. You did not opt in to SMS messages.
                    <br />We will contact you via other methods.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Compliance Notice */}
        <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Your Privacy Matters</h3>
              <p className="text-sm text-slate-600">
                We do not share, sell, or transfer your mobile phone number to third parties or affiliates for marketing or promotional purposes. 
                View our full <Link to="/privacy" className="text-teal-600 hover:underline">Privacy Policy</Link>.
              </p>
            </div>
          </div>
        </div>

        {/* Additional Info for Reviewers */}
        <div className="mt-6 text-center text-xs text-slate-400">
          <p>For opt-in documentation, visit: <Link to="/opt-in-evidence" className="text-teal-500 hover:underline">intakepal.com/opt-in-evidence</Link></p>
        </div>
      </main>
    </div>
  );
};

export default SMSSignupPage;
